import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import Path from 'routes/Path';
import { getConfig } from 'functions/getConfig';
import { useQueryParams } from 'hooks/useQueryParams';
import { useLanguageVersions } from 'hooks/useConfig';
import { useLoadFreecodeCreator, useFreecodeCreatorImplVersions } from 'hooks/useFreecode';
import { FreecodeLoading } from 'components/FreecodeLoading/FreecodeLoading';
import { identifiers } from 'components/FreecodePage/FreecodeLayoutConstants';
import { categories } from 'util/appConstants';
import FreecodeSummary from 'components/FreecodeContent/FreecodeDescription/FreecodeSummary';
import FreecodePage from 'components/FreecodePage/FreecodePage';
import { useFreecodeContext } from 'hooks/useContext';
import FreecodeEditorComponents from 'components/FreecodePage/FreecodeEditorComponents';
import PageUnderConstruction from 'components/FreecodePage/PageUnderConstruction';
import FreecodePreview from 'components/FreecodeContent/Creator/FreecodePreview/FreecodePreview';
import FreecodeEditorImplementation from 'components/FreecodeContent/FreecodeCodeEditor/FreecodeEditorImplementation';
import FreecodeApiSettings from 'components/FreecodeContent/Creator/FreecodeApiSettings/FreecodeApiSettings';
import FreecodeSettings from 'components/FreecodeContent/Creator/FreecodeSettings/FreecodeSettings';
import FreecodeGraphics from 'components/FreecodeContent/Creator/FreecodeGraphics/FreecodeGraphics';

const HEADER_LINKS = [{ link: Path.customTests, label: 'Create Page' }];

export const FreecodeCreatorContext = React.createContext();

export default function FreecodeCreator() {
  const { testId } = useParams();
  const { testImplId } = useQueryParams();

  const [loadingDone, setLoadingDone] = useState(false);
  const [loadingError, setLoadingError] = useState(undefined);
  const [headerLinks, setHeaderLinks] = useState(HEADER_LINKS);

  const { setTest, setTestImpl, setLanguageData, setCode, setIsCreator, setExerciseOptions } = useFreecodeContext();

  // Need language versions for performance cases and generated preview
  const [languageVersions /** ignore next */, , languageVersionsError] = useLanguageVersions();
  const [freecodeResponse /** ignore next */, , freecodeError] = useLoadFreecodeCreator(testId, testImplId);
  const [implVersions] = useFreecodeCreatorImplVersions(testId);

  useEffect(() => setIsCreator(true), [setIsCreator]);

  useEffect(() => {
    if (!testId) return;
    setHeaderLinks([
      ...HEADER_LINKS,
      {
        link:
          `${getConfig().REDIRECTION_ROOT_URL}/freecode-test-editor/${testId}` +
          (testImplId ? `?testImplId=${testImplId}` : ''),
        label: 'Old Creator',
      },
    ]);
  }, [testId, testImplId]);

  useEffect(() => {
    if (!freecodeResponse || !languageVersions) return;
    // When loading is done destructure the data
    try {
      const { test, testImpl } = freecodeResponse;
      setTest(test);
      setTestImpl(testImpl);
      setLanguageData(languageVersions?.[testImpl.executionVersion]);
      setLoadingDone(true);
      setCode(testImpl.solution || testImpl.generated?.skeletons?.python || 'Code not found...');
      if (test.category === categories.EXERCISE.category) {
        setExerciseOptions({
          levelToRun: 1,
          unlockedLevels: testImpl.configLevels.numPublic,
          nextAvailable: true,
        });
      }
    } catch (error) {
      setLoadingError('Could not unpack freecode data');
    }
  }, [freecodeResponse, languageVersions, setLanguageData, setTest, setTestImpl, setCode, setExerciseOptions]);

  const creatorComponents = useMemo(() => {
    if (!loadingDone) return {};
    return {
      [identifiers.summary]: <FreecodeSummary />,
      [identifiers.implementation]: <FreecodeEditorImplementation />,
      [identifiers.apiSettings]: <FreecodeApiSettings />,
      [identifiers.graphicsSettings]: <FreecodeGraphics />,
      [identifiers.settings]: <FreecodeSettings />,
      [identifiers.preview]: <FreecodePreview />,
    };
  }, [loadingDone]);

  return (
    <FreecodeCreatorContext.Provider value={{ implVersions }}>
      <FreecodePage headerLinks={headerLinks}>
        <FreecodeLoading
          signInRequired
          loading={!loadingDone}
          errors={[languageVersionsError, freecodeError, loadingError]}
          errorLinkText="All Custom Implementations"
          errorLink={Path.customTests}
        />
        <PageUnderConstruction />
        {loadingDone && <FreecodeEditorComponents parentComponents={creatorComponents} />}
      </FreecodePage>
    </FreecodeCreatorContext.Provider>
  );
}
